import { BACKGROUND_COLOR_DEFAULT, TEXT_COLOR_DEFAULT } from '../constants/colors';
import { FONT_DEFAULT_VALUE } from '../constants/fonts';
import { SPEAKER_LABEL_DEFAULT_TRANSITIONS } from '../constants/player';
import { SpeakerLabelStyles, SpeakerLabelTextStyle } from '../types/asset-types';
import { DEFAULT_NAME_FONT_SIZE_PRESET, DEFAULT_TITLE_FONT_SIZE_PRESET } from '../types/speaker-label-font-size';

export const getSpeakerLabelBorderRadiusDefault = () => '1em';

export const getSpeakerLabelBorderRadiusFull = () => '100cqh';

export const getSpeakerLabelDefaultContainerStyle = () => ({
  position: 'absolute' as const,
  bottom: '0.7cqw',
  left: '0.7cqw',
  minWidth: undefined,
  maxWidth: '90cqw',
  alignItems: 'flex-start' as const,
  overflow: 'hidden' as const,
  whiteSpace: 'nowrap' as const,
  lineHeight: 'none' as const,
  backgroundColor: BACKGROUND_COLOR_DEFAULT,
  borderRadius: getSpeakerLabelBorderRadiusDefault(),
  padding: '1cqi 0cqi',
  display: 'flex' as const,
  flexDirection: 'column' as const,
  zIndex: 10
});

export const getSpeakerLabelDefaultNameStyle = (): SpeakerLabelTextStyle => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSizePreset: DEFAULT_NAME_FONT_SIZE_PRESET,
  fontSize: undefined,
  lineHeight: 'none',
  fontFamily: 'inherit',
  maxWidth: '100%',
  color: TEXT_COLOR_DEFAULT
});

export const getSpeakerLabelDefaultTitleStyle = (): SpeakerLabelTextStyle => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSizePreset: DEFAULT_TITLE_FONT_SIZE_PRESET,
  fontSize: undefined,
  lineHeight: 'none',
  fontFamily: 'inherit',
  maxWidth: '100%',
  color: TEXT_COLOR_DEFAULT
});
export function migrateSpeakerLabelStyles(initialSpeakerLabelStyles: Partial<SpeakerLabelStyles> | undefined) {
  const hasNewCSSFields = !!(
    (initialSpeakerLabelStyles?.containerStyle && Object.keys(initialSpeakerLabelStyles.containerStyle).length > 0) ||
    (initialSpeakerLabelStyles?.nameStyle && Object.keys(initialSpeakerLabelStyles.nameStyle).length > 0) ||
    (initialSpeakerLabelStyles?.titleStyle && Object.keys(initialSpeakerLabelStyles.titleStyle).length > 0)
  );

  const borderRadius = initialSpeakerLabelStyles?.border_radius || getSpeakerLabelBorderRadiusDefault();
  const textColor = initialSpeakerLabelStyles?.text_color || TEXT_COLOR_DEFAULT;
  const backgroundColor = initialSpeakerLabelStyles?.background_color || BACKGROUND_COLOR_DEFAULT;

  const baseStyles = {
    font_location: initialSpeakerLabelStyles?.font_location || FONT_DEFAULT_VALUE.url,
    text_color: textColor,
    background_color: backgroundColor,
    border_radius: borderRadius
  };

  const animatedSpeakerLabelStyles = {
    title_font_location: initialSpeakerLabelStyles?.title_font_location || baseStyles.font_location,
    containerStyle: {
      ...getSpeakerLabelDefaultContainerStyle(),
      minWidth: undefined,
      backgroundColor,
      borderRadius: getSpeakerLabelBorderRadiusDefault(),
      padding: '1em 1.5em',
      ...initialSpeakerLabelStyles?.containerStyle
    },
    nameStyle: {
      ...getSpeakerLabelDefaultNameStyle(),
      color: textColor,
      ...initialSpeakerLabelStyles?.nameStyle
    },
    titleStyle: {
      ...getSpeakerLabelDefaultTitleStyle(),
      color: textColor,
      ...initialSpeakerLabelStyles?.titleStyle
    },
    transitions: initialSpeakerLabelStyles?.transitions || SPEAKER_LABEL_DEFAULT_TRANSITIONS
  };

  if (hasNewCSSFields) {
    // Already migrated: preserve existing CSS fields as-is
    return {
      ...baseStyles,
      title_font_location: initialSpeakerLabelStyles?.title_font_location,
      containerStyle: initialSpeakerLabelStyles?.containerStyle,
      nameStyle: initialSpeakerLabelStyles?.nameStyle,
      titleStyle: initialSpeakerLabelStyles?.titleStyle,
      transitions: initialSpeakerLabelStyles?.transitions || SPEAKER_LABEL_DEFAULT_TRANSITIONS
    } as SpeakerLabelStyles;
  }

  // Forward migration: apply defaults
  return { ...baseStyles, ...animatedSpeakerLabelStyles } as SpeakerLabelStyles;
}
